/**
 * Quantity controls of a cart line (snippets/cart-quantity.liquid), shared by the mini cart drawer
 * and the cart page.
 *
 * Instead of the theme flow (loader over the picture, then the whole drawer re-rendered, or a full
 * page reload on the cart page) the change is sent in the background, the pressed icon shows a
 * small spinner and only what changed is patched inside the closest [data-cart-root]: the line
 * (quantity, price, controls), the item count and the totals box. A removed line collapses at once
 * and the other lines move up while the request completes.
 *
 * Hooks expected in the markup:
 * - [data-cart-root] with data-cart-section (section to render) and optional data-cart-sections-url
 * - [data-cart-item][data-key] around each line, [data-cart-item-info] inside it
 * - [data-cart-count] for the item count, [data-cart-totals] for the totals box
 */
class CartQuantity extends HTMLElement {
  static REMOVE_DURATION = 300;

  connectedCallback() {
    this.addEventListener('click', this.onClick.bind(this));
    this.addEventListener('change', this.onChange.bind(this));
  }

  get root() {
    return this.closest('[data-cart-root]');
  }

  get item() {
    return this.closest('[data-cart-item]');
  }

  onClick(event) {
    const link = event.target.closest('a');

    if (!link || !this.contains(link)) {
      return;
    }

    event.preventDefault();

    const params = new URL(link.href, window.location.origin).searchParams;
    this.change(parseInt(params.get('quantity'), 10), params.get('line'), link);
  }

  onChange(event) {
    const input = event.target;

    if (!input.matches('input')) {
      return;
    }

    this.change(parseInt(input.value, 10) || 0, input.dataset.line, null);
  }

  async change(quantity, line, trigger) {
    if (this.classList.contains('cart-quantity--loading')) {
      return;
    }

    const root = this.root;
    const item = this.item;
    const isRemoval = quantity === 0;

    this.classList.add('cart-quantity--loading');
    trigger && trigger.classList.add('cart-quantity__button--loading');

    // Optimistic removal: the line folds right away and the others move up.
    const collapse = isRemoval ? CartQuantity.collapse(item) : Promise.resolve();

    try {
      const body = { line, quantity, sections: [root ? root.dataset.cartSection : 'mini-cart'] };

      if (root && root.dataset.cartSectionsUrl) {
        body.sections_url = root.dataset.cartSectionsUrl;
      }

      const response = await fetch(`${window.themeVariables.routes.cartChangeUrl}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        throw new Error(`Cart change failed with status ${response.status}`);
      }

      const cart = await response.json();

      await collapse;
      CartQuantity.patch(root, cart);

      document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
        bubbles: true,
        detail: { cart }
      }));
    } catch (error) {
      // Fall back to the theme refresh (drawer) or a reload (cart page) so nothing stays out of sync.
      this.classList.remove('cart-quantity--loading');
      trigger && trigger.classList.remove('cart-quantity__button--loading');

      if (root && root.tagName === 'CART-DRAWER') {
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      } else {
        window.location.reload();
      }
    }
  }

  // Fades the line out; in a vertical list it also folds so the lines below move up.
  static collapse(item) {
    if (!item) {
      return Promise.resolve();
    }

    const list = item.parentElement;
    const isGrid = list && getComputedStyle(list).display.includes('grid');
    const rowGap = list ? parseFloat(getComputedStyle(list).rowGap) || 0 : 0;

    const frames = isGrid
      ? [{ opacity: 1 }, { opacity: 0 }]
      : [
          { opacity: 1, height: `${item.offsetHeight}px`, marginBottom: '0px' },
          { opacity: 0, height: '0px', marginBottom: `-${rowGap}px` }
        ];

    const animation = item.animate(frames, {
      duration: CartQuantity.REMOVE_DURATION,
      easing: 'ease',
      fill: 'forwards'
    });

    return animation.finished.then(() => item.remove());
  }

  /**
   * Updates the root from the freshly rendered section: lines are matched by their cart key so the
   * scroll position, the discount form and everything else are left untouched.
   */
  static patch(root, cart) {
    const html = cart.sections && root && cart.sections[root.dataset.cartSection];

    if (!html) {
      return;
    }

    const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-root]');

    if (!fresh) {
      return;
    }

    const freshItems = fresh.querySelectorAll('[data-cart-item]');
    const currentItems = root.querySelectorAll('[data-cart-item]');

    // Empty cart (or a cart that just got its first line): the layout changes completely.
    if (freshItems.length === 0 || currentItems.length === 0) {
      root.innerHTML = fresh.innerHTML;
      return;
    }

    const count = root.querySelector('[data-cart-count]');
    const freshCount = fresh.querySelector('[data-cart-count]');

    if (count && freshCount) {
      count.textContent = freshCount.textContent;
    }

    const list = currentItems[0].parentElement;
    const seen = new Set();

    freshItems.forEach((freshItem) => {
      const key = freshItem.dataset.key;
      const current = root.querySelector(`[data-cart-item][data-key="${CSS.escape(key)}"]`);

      seen.add(key);

      if (current) {
        current.querySelector('[data-cart-item-info]').innerHTML = freshItem.querySelector('[data-cart-item-info]').innerHTML;
      } else if (list) {
        list.appendChild(freshItem);
      }
    });

    currentItems.forEach((item) => {
      if (!seen.has(item.dataset.key)) {
        item.remove();
      }
    });

    ['[data-cart-totals]', '.free-gift'].forEach((selector) => {
      const current = root.querySelector(selector);
      const replacement = fresh.querySelector(selector);

      if (current && replacement) {
        current.replaceWith(replacement);
      }
    });
  }
}

window.customElements.define('cart-quantity', CartQuantity);
